import { Palette } from '@vibrant/color';
import { Component, EventEmitter, Input, Output, ViewChild, inject } from '@angular/core';
import { ReportConfig, ReportTemplate } from '@modules/report-generator/interfaces';
import { Themes } from '@modules/report-generator/themes';
import { TextLanguage } from '@modules/report-generator/types';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import { MatDialogRef } from '@angular/material/dialog';
import { SaveTemplateDialogComponent } from '../../shared/save-template-dialog/save-template-dialog.component';
import { SaveTemplateFormData } from '@shared/interfaces/save-template-dialog.interface';
import { ConfirmDialogComponent, ConfirmDialogModel } from '@shared/dialogs/confirm-dialog/confirm-dialog.component';
import { ReportGeneratorStore } from '@modules/report-generator/report-generator.store';
import { ReportGeneratorService } from '@modules/report-generator/report-generator.service';
import { MatSelect } from '@angular/material/select';

@Component({
  selector: 'app-config-panel',
  templateUrl: './config-panel.component.html',
  styleUrls: ['./config-panel.component.scss'],
})
export class ConfigPanelComponent {
  @ViewChild('templateSelect') templateSelect: MatSelect;

  private readonly dialog = inject(MatDialog);
  private readonly store = inject(ReportGeneratorStore);
  private readonly service = inject(ReportGeneratorService);

  @Input() isOpen = false;
  @Input() reportConfig?: ReportConfig | null;
  @Input() templates?: ReportTemplate[] | null;

  themes = Themes.map((theme) => ({ name: theme.name, value: theme.slug }));

  onLanguageChange(language: string): void {
    this.store.updateConfig({ language: language as TextLanguage });
  }

  onTemplateSelectionChange(templateId: number): void {
    const currentTemplateId = this.reportConfig?.template?.id;
    if (templateId === currentTemplateId) {
      return;
    }

    const hasChanges =
      this.reportConfig?.changes && Array.isArray(this.reportConfig?.changes) && this.reportConfig?.changes.length > 0;

    if (hasChanges) {
      const dialogRef: MatDialogRef<SaveTemplateDialogComponent> = this.dialog.open(SaveTemplateDialogComponent, {
        width: '480px',
        panelClass: 'general-dialog',
        autoFocus: true,
        disableClose: true,
        data: {
          templateName: this.reportConfig?.template?.name!,
          isGlobal: !this.reportConfig?.template?.organization,
          hasChanges,
        },
      });

      dialogRef.afterClosed().subscribe((result: SaveTemplateFormData) => {
        if (!result) {
          this.templateSelect.value = currentTemplateId;
          return;
        }

        const newTemplate = {
          ...this.reportConfig?.template!,
          name: result.templateName,
        };

        if (!result.shouldContinue) {
          this.store.saveTemplate(newTemplate, result.shouldClone);
        }
        this.store.selectTemplate(templateId);
      });
    } else {
      this.store.selectTemplate(templateId);
    }
  }

  onThemeChange(theme: string): void {
    if (!this.reportConfig?.template) {
      return;
    }

    const newTheme = Themes.find((t) => t.slug === theme);
    if (!newTheme) {
      return;
    }

    const updatedTemplate = {
      ...this.reportConfig.template,
      theme: newTheme,
    };
    this.store.updateConfig({ template: updatedTemplate });
  }

  onThemeColorChange(colorType: 'primary' | 'secondary', color: string): void {
    if (!this.reportConfig?.template?.theme) {
      return;
    }

    const currentTheme = this.reportConfig.template.theme;
    const currentColors = currentTheme.colors || {};
    const updatedTemplate = {
      ...this.reportConfig.template,
      theme: {
        ...currentTheme,
        colors: {
          ...currentColors,
          [colorType]: color,
        },
      },
    };
    this.store.updateConfig({ template: updatedTemplate });
  }

  swapColors(): void {
    if (!this.reportConfig?.template?.theme) {
      return;
    }

    const currentColors = this.reportConfig.template.theme?.colors;
    if (!currentColors) {
      return;
    }

    const primaryColor = currentColors.primary!;
    const secondaryColor = currentColors.secondary!;

    this.onThemeColorChange('primary', secondaryColor);
    setTimeout(() => {
      return this.onThemeColorChange('secondary', primaryColor);
    }, 50);
  }

  async onExtractColors(): Promise<void> {
    try {
      const palette = await this.service.extractColorsFromOrgLogo(this.reportConfig!);

      if (!palette) {
        console.warn('No colors extracted from logo');
        return;
      }

      // Extract primary color from palette (use Vibrant as primary)
      const primaryColor = palette.Vibrant?.hex;
      const secondaryColor = palette.DarkVibrant?.hex;

      if (primaryColor) {
        this.onThemeColorChange('primary', primaryColor);
      }
      if (secondaryColor) {
        setTimeout(() => {
          return this.onThemeColorChange('secondary', secondaryColor);
        }, 50);
      }
    } catch (error) {
      console.error('Failed to extract colors:', error);
    }
  }

  getCurrentTemplateId(): number | null {
    return this.reportConfig?.template?.id || null;
  }

  getCurrentTheme(): string {
    return this.reportConfig?.template?.theme?.slug ?? 'professional';
  }

  showDeleteButton(): boolean {
    return Boolean(this.reportConfig?.template?.organization);
  }

  onDeleteTemplate(): void {
    const message = 'Are you sure you want to delete this template?';
    const dialogData = new ConfirmDialogModel('Confirm deletion', message, 'Delete', 'Cancel');
    const dialogRef: MatDialogRef<ConfirmDialogComponent> = this.dialog.open(ConfirmDialogComponent, {
      width: '400px',
      data: dialogData,
      disableClose: true,
    });

    dialogRef.afterClosed().subscribe((result: SaveTemplateFormData) => {
      if (!result) {
        return;
      }

      this.store.deleteTemplate(this.reportConfig?.template?.id!);
    });
  }

  onSaveTemplate(): void {
    const dialogRef: MatDialogRef<SaveTemplateDialogComponent> = this.dialog.open(SaveTemplateDialogComponent, {
      width: '480px',
      panelClass: 'general-dialog',
      autoFocus: true,
      disableClose: true,
      data: {
        templateName: this.reportConfig?.template?.name!,
        isGlobal: !this.reportConfig?.template?.organization,
      },
    });

    dialogRef.afterClosed().subscribe((result: SaveTemplateFormData) => {
      if (!result) {
        return;
      }

      const newTemplate = {
        ...this.reportConfig?.template!,
        name: result.templateName,
      };
      this.store.saveTemplate(newTemplate, result.shouldClone);
    });
  }
}
